import React, {useEffect, useCallback, useRef, useState} from 'react'
import {OrderBoxWrap} from './styles'
import Button from '../../module/button'
import BorderInput from '../inputBox/BorderInput'
import Select from '../../module/select'
import {OrderAction} from '../../store/action'
import {useRecoilValue, useSetRecoilState} from 'recoil'
import {authAtom} from '../../store/recoil/auth'
import {useLocation, useNavigate} from 'react-router'
import {alertAtom} from '../../store/recoil/alert'
import {OrderPriceAction} from '../../store/action/orderPrice'
import Input from '../../admin/module/input'
import PostCodeModal from '../postCodeModal/PostCodeModal'
import {Address} from 'react-daum-postcode'

interface AddressInfoInterface {
  address: string | null
  addressCode: string | null
  addressDetail: string | null
}

interface PayverseReadyResponse {
  mid: string
  clientKey: string
  reqDate: string
  orderId: string
  productName: string
  requestCurrency: string
  requestAmount: string
  customerName?: string
  customerId?: string
  mallReserved?: string
  returnUrl: string
  appScheme?: string
  webhookUrl?: string
  sign: string
}

const PAYVERSE_PENDING_ORDER_KEY = 'payverse.pendingOrder'

declare global {
  interface Window {
    payVerse?: {
      requestUI: (
        params: Record<string, unknown>,
        callback?: (result: Record<string, unknown>) => void
      ) => void
    }
  }
}

const PAYVERSE_SDK_URL = process.env.REACT_APP_PAYVERSE_SDK_URL || 'https://ui-snd.payverseglobal.com/js/payments.js'

const loadPayverseSdk = async (): Promise<void> => {
  if (window.payVerse?.requestUI) {
    return
  }

  await new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${PAYVERSE_SDK_URL}"]`)

    if (existingScript) {
      if (window.payVerse?.requestUI) {
        resolve()
        return
      }

      existingScript.addEventListener('load', () => resolve(), {once: true})
      existingScript.addEventListener('error', () => reject(new Error('PAYVERSE SDK load failed')), {once: true})
      return
    }

    const script = document.createElement('script')
    script.src = PAYVERSE_SDK_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('PAYVERSE SDK load failed'))
    document.head.appendChild(script)
  })
}

const OrderBox = () => {

  // 로그인 한 유저
  const auth = useRecoilValue(authAtom)

  const [address, setAddress] = useState<AddressInfoInterface>({
    address: null,
    addressCode: null,
    addressDetail: null,
  })
  const [openPostCodeModal, setOpenPostCodeModal] = useState<boolean>(false)
  // Location
  const location = useLocation()

  // Navigate
  const navigate = useNavigate()

  // Alert
  const setAlert = useSetRecoilState(alertAtom)

  // Order Action
  const {order} = OrderAction()
  const {getOrderPirceTitle} = OrderPriceAction()

  // 결제할 상품 옵션
  const options = location?.state?.shopOptions

  // 결제 상품 정보
  const shopDetail = location?.state?.shopDetail

  // 결제 상품 정보
  const count = location?.state?.count

  // 결제 정보들
  const [orderInfo, setOrderInfo] = useState<any>({
    orderName: '',
    // 리얼상품을 위해 임시로..
    product_id: shopDetail.id,
    type: shopDetail.type,
    user_id: 0,
    total: 0,
    pay_method: '',
    totalCnt: 0,
    receiver_name: '',
    receiver_phone: '',
    address: {
      address: null,
      addressCode: null,
      addressDetail: null,
    },
    options: [
      {
        id: 0,
        cnt: 0,
        original_price: 0,
        tax: 0,
        parent: 0,
      },
    ],
  })

  // 쿠폰 받을 휴대전화
  const [selectValue, setSelectValue] = useState('010')
  const [phone, setPhone] = useState('')
  const optionList = [
    {
      value: '010',
      label: '010',
    },
    {
      value: '011',
      label: '011',
    },
    {
      value: '016',
      label: '016',
    },
    {
      value: '019',
      label: '019',
    },
  ]

  const handleSelectChange = (v: string) => {
    setSelectValue(v)
  }

  const handleGetAddress = (data: Address) => {
    setAddress(prev => ({
      ...prev,
      addressCode: data.zonecode,
      address: data.address,
    }))
  }

  // 주문 결제 시 상품 이름
  const [orderProductName, setOrderProductName] = useState('')
  const [payversePayload, setPayversePayload] = useState<PayverseReadyResponse | null>(null)
  const [payversePrepareError, setPayversePrepareError] = useState('')
  const finalizePromiseRef = useRef<Promise<unknown> | null>(null)

  const finalizeOrder = useCallback((paymentPayload?: Record<string, any>) => {
    if (finalizePromiseRef.current) {
      return finalizePromiseRef.current
    }

    const paymentOrderInfo = paymentPayload
      ? {
        ...orderInfo,
        service_id: paymentPayload?.schemeGroup || 'PAYVERSE',
        order_id: paymentPayload?.orderId || '',
        track_id: paymentPayload?.tid || '',
        amount: String(orderInfo.total),
        trxdate: '',
        authcd: paymentPayload?.resultCode || '',
        response_code: paymentPayload?.resultCode || '',
      }
      : orderInfo

    const finalizePromise = order(paymentOrderInfo)
      .then((response) => {
        if (!response) {
          throw new Error('주문 저장에 실패했습니다.')
        }

        sessionStorage.removeItem(PAYVERSE_PENDING_ORDER_KEY)
        navigate('/my/order')
        return response
      })
      .catch((error) => {
        finalizePromiseRef.current = null
        setAlert({
          title: '오류',
          type: 'alert',
          message: '주문 처리 중 오류가 발생했습니다.',
          isShow: true,
        })
        console.error('Order error:', error)
        throw error
      })

    finalizePromiseRef.current = finalizePromise
    return finalizePromise
  }, [navigate, order, orderInfo, setAlert])

  // 결제하기
  const handleSubmit = () => {
    try {
      if (!payversePayload) {
        throw new Error(payversePrepareError || '결제 정보를 준비 중입니다. 잠시 후 다시 시도해 주세요.')
      }

      if (!window.payVerse?.requestUI) {
        throw new Error('Payverse SDK를 초기화하지 못했습니다.')
      }

      sessionStorage.setItem(PAYVERSE_PENDING_ORDER_KEY, JSON.stringify(orderInfo))

      window.payVerse.requestUI(
        {
          ...payversePayload,
          billkeyReq: 'N',
        },
        (result) => {
          const resultStatus = String(result?.resultStatus || '')

          if (resultStatus === 'SUCCESS') {
            finalizeOrder(result).catch(() => undefined)
            return
          }

          setAlert({
            title: '결제 실패',
            type: 'alert',
            message: String(result?.resultMessage || '결제가 취소되었거나 실패했습니다.'),
            isShow: true,
          })
        }
      )
    } catch (error: any) {
      setAlert({
        title: '결제 오류',
        type: 'alert',
        message: error?.message || '결제창을 불러오지 못했습니다.',
        isShow: true,
      })
    }
  }

  // 총 주문 금액 / 카운트
  let totalPrice = 0
  let totalCnt = 0

  if (shopDetail?.type !== 1) {
    totalPrice = options?.reduce((acc: any, cur: any) => acc + ((cur.cnt || 0) * (cur.original_price + cur.tax)), 0)
    totalCnt = options?.reduce((acc: any, cur: any) => acc + cur.cnt, 0)
  } else if (options?.some((item: any) => item.cnt > 0)) {
    totalPrice = options?.reduce((acc: any, cur: any) => acc + ((cur.cnt || 0) * Number(cur.original_price || shopDetail?.price || 0)), 0)
    totalCnt = options?.reduce((acc: any, cur: any) => acc + (cur.cnt || 0), 0)
  } else {
    totalPrice = shopDetail?.price * count
    totalCnt = count
  }

  const handleUpdateInfo = (k: string, v: string | number) => setAddress(prev => (
    {...prev, [k]: v}),
  )

  const resolvedAddress = {
    address: address.address || auth?.address0 || '',
    addressCode: address.addressCode || auth?.post || '',
    addressDetail: address.addressDetail || auth?.address1 || '',
  }

  // 상품 정보 가져오기
  useEffect(() => {
    getOrderPirceTitle(totalPrice)
      .then((res) => {
        setOrderProductName(res.payload)
        setOrderInfo({
          ...orderInfo,
          orderName: res.payload,
          user_id: auth?.id,
          total: totalPrice,
          pay_method: '카드결제',
          totalCnt: totalCnt,
          receiver_name: auth?.name,
          //receiver_phone: selectValue + phone,
          receiver_phone: auth?.phone,
          options: options,
          address: resolvedAddress
        })
      })

    setSelectValue(auth?.phone?.slice(0, 3))
    setPhone(auth?.phone?.slice(3, 11))
  }, [auth, options, address])

  useEffect(() => {
    const normalizedProductName = (
      orderProductName ||
      shopDetail?.name ||
      orderInfo?.orderName ||
      '주문상품'
    ).trim()
    const normalizedAmount = Number(orderInfo?.total || totalPrice || 0)

    if (!normalizedProductName || normalizedAmount <= 0 || !auth?.id) {
      return
    }

    let isCancelled = false
    setPayversePayload(null)
    setPayversePrepareError('')

    const preparePayment = async () => {
      try {
        await loadPayverseSdk()

        const response = await fetch(`${process.env.REACT_APP_BASE_URL}cert/payverse/ready`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productName: normalizedProductName,
            amount: normalizedAmount,
            customerName: auth?.name,
            customerId: String(auth.id),
            mallReserved: String(shopDetail?.id || ''),
          }),
        })

        const preparedPayload = await response.json() as PayverseReadyResponse & {message?: string}

        if (!response.ok) {
          throw new Error(preparedPayload?.message || 'Payverse 결제 준비에 실패했습니다.')
        }

        if (!isCancelled) {
          setPayversePayload(preparedPayload)
        }
      } catch (error: any) {
        if (!isCancelled) {
          setPayversePrepareError(error?.message || '결제 정보를 준비하지 못했습니다.')
        }
      }
    }

    preparePayment()

    return () => {
      isCancelled = true
    }
  }, [auth?.id, auth?.name, orderInfo?.orderName, orderInfo?.total, orderProductName, shopDetail?.id, shopDetail?.name, totalPrice])

  // 상품 정보 없이 접근 시 home으로 보내기
  useEffect(() => {
    if (!options) {
      navigate('/')
      setAlert({
        title: '오류',
        type: 'alert',
        message: '정상적이지 않은 접근입니다.',
        isShow: true,
      })
    }
  }, [auth, location])

  // postMessage 이벤트 리스너
  const handleMessage = useCallback((event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return
      }

      const { type, success, provider, payload } = event.data;

      if (type === 'payment' && success) {
        finalizeOrder(provider === 'payverse' ? payload : undefined).catch(() => undefined)
      }
      if (type === 'payment' && !success) {
        setAlert({
          title: '결제 실패',
          type: 'alert',
          message: payload?.resultMessage || '결제가 취소되었거나 실패했습니다.',
          isShow: true,
        })
      }
  }, [finalizeOrder, setAlert]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  return (
    <>
      <OrderBoxWrap>
        {
          shopDetail.type === 1 && <div className={'address'}>
            <span>주소</span>
            <label htmlFor="zip-code">
              <Input
                id="addressCode"
                name="addressCode"
                placeholder="우편번호"
                value={address.addressCode ? address.addressCode : auth.post}
                disabled={true}
              />
              <button type="button" className="text-btn" onClick={() => setOpenPostCodeModal(true)}>
                우편번호 찾기
              </button>
            </label>
            <label htmlFor="address01">
              <Input
                id="address01"
                name="address01"
                placeholder="주소"
                value={address.address ? address.address : auth.address0}
                disabled={true}
              />
            </label>
            <label htmlFor="address02">
              <Input
                id="address02"
                name="address02"
                placeholder="상세 주소를 입력해 주세요"
                value={address.addressDetail ? address.addressDetail : auth.address1}
                onChange={e => handleUpdateInfo('addressDetail', e.target.value)}
              />
            </label>
          </div>
        }

        <div className={'wrapper'}>
          <div className="left-box">
            <div className="order-type">
              {/*<MainPay */}
              {/*  username={auth?.name} */}
              {/*  amount={totalPrice?.toString()} */}
              {/*  goodsname={orderProductName || shopDetail?.name}*/}
              {/*/>*/}
              <Button
                text="신용카드결제"
                borderColor="#d9d9d9"
                textColor="rgba(0, 0, 0, 0.87)"
                backgroundColor="#d9d9d9"
                icon="/image/card-icon.png"
                boxShadow="0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12)"
              />
            </div>
            {
              shopDetail?.type !== 1 &&
                <div className="coupon-wrap">
                    <h4>안내 받을 번호</h4>
                    <div className="phone-wrap">
                      <Select
                        optionList={optionList}
                        value={selectValue}
                        handleChange={handleSelectChange}
                        borderType={true}
                        disabled={true}
                      />
                      <BorderInput
                        title="-없이 숫자만 입력"
                        closeNone={true}
                        value={phone}
                        handleChange={(v) => setPhone(v)}
                        disabled={true}
                      />
                    </div>
                  <div className="info-text">
                    <span>* 입력한 번호로 안내문이 발송됩니다. 정확하게 입력해주세요.</span>
                    {/* <span>* 휴대폰 결제는 입력한 번호로만 결제할 수 있습니다.</span> */}
                  </div>
                </div>
            }
          </div>
          <div className="right-box">
            <div className="price-wrap">
              <dl>
                <dt>총 상품가격</dt>
                <dd>{Number(totalPrice).toLocaleString('ko-KR')} 원</dd>
              </dl>
              <dl>
                <dt>총 결제 예상 금액</dt>
                <dd>{Number(totalPrice).toLocaleString('ko-KR')} 원</dd>
              </dl>
            </div>
            <Button
              text="결제하기"
              borderColor="#57849e"
              textColor="#fff"
              backgroundColor="#57849e"
              handleClick={handleSubmit}
            />
          </div>
        </div>
      </OrderBoxWrap>
      {
        openPostCodeModal ? <PostCodeModal
          onClose={() => setOpenPostCodeModal(false)}
          onAddress={handleGetAddress}
        /> : null
      }
    </>
  )
}

export default OrderBox
